/**
 * 화면 셸의 표시값 — 화이트라벨 대행사 호스트면 대행사 것, 아니면 liveGPT(261025-agency-white-label).
 *
 * 서버가 `index.html` 에 `<script type="application/json" id="geo-branding">` 로 넣는다(대행사 호스트에서만).
 * 별도 API 를 부르지 않는 이유: 첫 화면이 liveGPT 로 깜빡이지 않게. 실행되지 않는 데이터 블록이라 CSP(`script-src 'self'`)를
 * 바꾸지 않는다.
 *
 * ★ **셸(머리·바닥·로그인·고객 지원)은 이것을 쓴다.** `BRAND`·`COMPANY`(lib/company.ts)는 **우리 약관 문서**의 값이라
 *   그대로 둔다 — 대행사 호스트의 약관 화면은 우리 약관을 보이지 않고 대행사 문서로 안내한다.
 * ★ 화이트라벨 화면에 liveGPT·비하이브가 보이면 안 된다 — 값이 없는 칸(소개 링크·카카오 채널·운영사 인증)은 **숨긴다**.
 */
import { BrandingView } from '@geo-admin/shared/contract/agency.ts';
import { BRAND, COMPANY } from './company.ts';

function readInjected(): BrandingView | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById('geo-branding');
  if (!el?.textContent) return null;
  try {
    const parsed = BrandingView.safeParse(JSON.parse(el.textContent));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** 화이트라벨 표시값. null 이면 liveGPT 화면이다 */
export const WHITE_LABEL: BrandingView | null = readInjected();

export interface Shell {
  serviceName: string;
  /** 로고 이미지 경로 — 없으면 이름 글자. liveGPT 는 심볼+워드마크(LogoMark) */
  logoPath: string | null;
  /** 서비스 소개 링크 — liveGPT 는 livegpt.co.kr, 화이트라벨은 같은 호스트의 랜딩(`/`) */
  homepage: string | null;
  tagline: string;
  descriptor: string;
  company: {
    name: string;
    ceo: string | null;
    registrationNo: string | null;
    mailOrderNo: string | null;
    address: string | null;
  };
  support: { tel: string | null; email: string | null; kakaoUrl: string | null };
  /** 운영사 공인 기록 — liveGPT 만 */
  credentials: readonly string[];
  /** 화이트라벨의 약관·개인정보처리방침 — 대행사 문서. liveGPT 는 우리 화면(/terms·/privacy) */
  termsUrl: string | null;
  privacyUrl: string | null;
}

export const SHELL: Shell = WHITE_LABEL
  ? {
      serviceName: WHITE_LABEL.branding.serviceName,
      logoPath: WHITE_LABEL.branding.logoPath,
      // 대행사 랜딩 — 같은 호스트의 `/` 를 서버가 내준다(리포트 앱 안 공통 랜딩 틀)
      homepage: '/',
      // 서비스 이름이 들어가지 않는 일반 문구라 그대로 쓴다
      tagline: BRAND.tagline,
      descriptor: WHITE_LABEL.branding.serviceName,
      company: {
        name: WHITE_LABEL.legal.companyName,
        ceo: WHITE_LABEL.legal.ceo,
        registrationNo: WHITE_LABEL.legal.businessNo,
        mailOrderNo: null,
        address: WHITE_LABEL.legal.address,
      },
      support: {
        tel: WHITE_LABEL.branding.supportTel,
        email: WHITE_LABEL.branding.supportEmail,
        kakaoUrl: WHITE_LABEL.branding.supportKakaoUrl,
      },
      credentials: [],
      termsUrl: WHITE_LABEL.legal.termsUrl,
      privacyUrl: WHITE_LABEL.legal.privacyUrl,
    }
  : {
      serviceName: BRAND.name,
      logoPath: null,
      homepage: BRAND.homepage,
      tagline: BRAND.tagline,
      descriptor: BRAND.descriptor,
      company: {
        name: COMPANY.name,
        ceo: COMPANY.ceo,
        registrationNo: COMPANY.registrationNo,
        mailOrderNo: COMPANY.mailOrderNo,
        address: COMPANY.address,
      },
      support: { tel: COMPANY.tel, email: COMPANY.email, kakaoUrl: COMPANY.kakaoChannelUrl },
      credentials: COMPANY.credentials,
      termsUrl: null,
      privacyUrl: null,
    };
